import React, { useState } from "react";
import {
    Container,
    Grid,
    Button,
    Typography,
    Box,
    Autocomplete,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";

const customers = [
    { id: 1, name: "Abdur Rahim", mobile_no: "017xxxxxxxx" },
    { id: 2, name: "Sufia Begum", mobile_no: "018xxxxxxxx" },
    // ...dynamic data
];
const products = [
    { id: 1, name: "Paracetamol", pack_size: "10x10", measurementUnit: "Tablet" },
    { id: 2, name: "Amoxicillin", pack_size: "10x10", measurementUnit: "Capsule" },
    // ...dynamic data
];

// Table columns
const columns = [
    "Name of Product",
    "Pack Size",
    "Quantity",
    "Sample",
    "Bonus",
    "Trade Price",
    "Total Price",
];

const CustProSalesReport = () => {
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [tableRows, setTableRows] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);

    // DataTable features
    const [searchTerm, setSearchTerm] = useState("");
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [downloading, setDownloading] = useState(false);

    // Table search/filter
    const filteredRows = tableRows.filter((row) => {
        const query = searchTerm.toLowerCase();
        return (
            row.product_name?.toLowerCase().includes(query) ||
            row.pack_size?.toLowerCase().includes(query)
            // Expand fields here if needed
        );
    });

    // Pagination logic
    const totalEntries = filteredRows.length;
    const totalPages = Math.ceil(totalEntries / entriesPerPage);
    const indexOfLastRow = currentPage * entriesPerPage;
    const indexOfFirstRow = indexOfLastRow - entriesPerPage;
    const displayedRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);

    const startEntry = totalEntries === 0 ? 0 : indexOfFirstRow + 1;
    const endEntry = Math.min(indexOfLastRow, totalEntries);

    // Handlers
    const handleSearch = (e) => {
        e.preventDefault();
        setTableLoading(true);

        setTimeout(() => {
            if (selectedCustomer && fromDate && toDate) {
                setTableRows([
                    {
                        product_name: "Paracetamol",
                        pack_size: "10x10 Tablet",
                        quantity: 50,
                        sample_qty: 5,
                        bonus_qty: 2,
                        sale_unit_price: 100,
                        total_price: 5000,
                    },
                    {
                        product_name: "Amoxicillin",
                        pack_size: "10x10 Capsule",
                        quantity: 20,
                        sample_qty: 2,
                        bonus_qty: 1,
                        sale_unit_price: 80,
                        total_price: 1600,
                    },
                ]);
            } else {
                setTableRows([]);
            }
            setTableLoading(false);
            setCurrentPage(1);
            setSearchTerm("");
        }, 1000);
    };

    const handleClearProduct = () => setSelectedProduct(null);

    const handleEntriesChange = (event) => {
        setEntriesPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleTableSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

    // Download Report Button
    const handleDownloadReport = () => {
        setDownloading(true);
        setTimeout(() => {
            setDownloading(false);
            alert("Report downloaded!");
        }, 1200);
    };

    // Row background color logic for alternate rows
    const getRowStyle = (idx) =>
        idx % 2 === 0
            ? { backgroundColor: "#f9f9f9" } // off-white
            : { backgroundColor: "#fff" };   // default white

    return (
        <Box sx={{ background: "#fff", minHeight: "100vh", py: 4 }}>
            <Container maxWidth="lg">
                <Typography variant="h5" fontWeight="bold" color="primary" mb={2}>
                    Sales Report Search
                </Typography>
                {/* Main Form: untouched */}
                <Box component="form" onSubmit={handleSearch} mb={4}>
                    <Grid container spacing={2} alignItems="center">
                        {/* Customer Dropdown */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ minWidth: 250, maxWidth: 400, width: "100%" }}>
                                <Autocomplete
                                    options={customers}
                                    getOptionLabel={(option) =>
                                        `${option.name}${option.mobile_no ? " " + option.mobile_no : ""}`
                                    }
                                    value={selectedCustomer}
                                    onChange={(e, v) => setSelectedCustomer(v)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Customer" variant="outlined" fullWidth

                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    height: "44px",
                                                    paddingTop: "0px",
                                                    paddingBottom: "0px",
                                                },
                                                "& .MuiInputLabel-outlined": {
                                                    transform: "translate(14px, 14px) scale(1)",
                                                },
                                                "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
                                                    transform: "translate(14px, -6px) scale(0.75)",
                                                },
                                            }}

                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            minWidth: 250,
                                            maxWidth: 400,
                                            width: "100%",
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                        {/* Product Dropdown */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ minWidth: 250, maxWidth: 400, width: "100%" }}>
                                <Autocomplete
                                    options={products}
                                    getOptionLabel={(option) =>
                                        `${option.name} ${option.pack_size} ${option.measurementUnit}`
                                    }
                                    value={selectedProduct}
                                    onChange={(e, v) => setSelectedProduct(v)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Select Product" variant="outlined" fullWidth

                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    height: "44px",
                                                    paddingTop: "0px",
                                                    paddingBottom: "0px",
                                                },
                                                "& .MuiInputLabel-outlined": {
                                                    transform: "translate(14px, 14px) scale(1)",
                                                },
                                                "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
                                                    transform: "translate(14px, -6px) scale(0.75)",
                                                },
                                            }}
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}

                                />
                            </Box>
                        </Grid>
                     
                        {/* Search Button */}
                        <Grid item xs={12} sm={6} md={2}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="info"
                                fullWidth
                                sx={{
                                    //   height: "56px",
                                    height: "44px",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    boxShadow: 1,
                                    "&:hover": { background: "#1976d2", color: "#fff" },
                                }}
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {/* DataTable controls: Download Report TOP RIGHT */}
                {tableRows.length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                        }}
                    >
                        {/* Left side: Show Entries + Search */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            {/* Show Entries Dropdown */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2">Show</Typography>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                        value={entriesPerPage}
                                        onChange={handleEntriesChange}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                        <MenuItem value={100}>100</MenuItem>
                                    </Select>
                                </FormControl>
                                <Typography variant="body2">entries</Typography>
                            </Box>
                            {/* Search Input for Table */}
                            <TextField
                                label="Search"
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={handleTableSearch}
                                sx={{ width: { xs: "100%", sm: "250px" } }}
                            />
                        </Box>
                        {/* Right side: Download Report */}
                        {filteredRows.length > 0 && (
                            <Button
                                variant="outlined"
                                color="success"
                                onClick={handleDownloadReport}
                                disabled={downloading}
                                sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                            >
                                {downloading ? "Downloading..." : "Download Report"}
                            </Button>
                        )}
                    </Box>
                )}

                {/* Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((col, idx) => (
                                    <TableCell
                                        key={col}
                                        align="center"
                                        sx={{
                                            fontWeight: "bold",
                                            whiteSpace: "nowrap",
                                            fontSize: "1.05rem",
                                            borderRight: idx !== columns.length - 1 ? "1px solid #e0e0e0" : undefined, // vertical line
                                            background: "#f5f5f5", // table header color
                                        }}
                                    >
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            ) : displayedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ color: "red" }}>
                                        No record found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayedRows.map((row, idx) => (
                                    <TableRow
                                        key={idx}
                                        sx={{
                                            ...getRowStyle(idx),
                                            borderBottom: "1px solid #e0e0e0", // horizontal line between rows
                                        }}
                                    >
                                        <TableCell align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>{row.product_name}</TableCell>
                                        <TableCell align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>{row.pack_size}</TableCell>
                                        <TableCell align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>{row.quantity}</TableCell>
                                        <TableCell align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>{row.sample_qty}</TableCell>
                                        <TableCell align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>{row.bonus_qty}</TableCell>
                                        <TableCell align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>{row.sale_unit_price}</TableCell>
                                        <TableCell align="center">{row.total_price}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {tableRows.length > 0 && filteredRows.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "center", sm: "center" },
                            gap: 2,
                            mt: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Showing {startEntry} to {endEntry} of {totalEntries} entries
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button variant="outlined" onClick={handlePrevPage} disabled={currentPage === 1}>
                                Prev
                            </Button>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <Button
                                    key={i + 1}
                                    variant={currentPage === i + 1 ? "contained" : "outlined"}
                                    color={currentPage === i + 1 ? "primary" : "inherit"}
                                    onClick={() => setCurrentPage(i + 1)}
                                    sx={{ minWidth: 40, px: 1 }}
                                >
                                    {i + 1}
                                </Button>
                            ))}
                            <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0}>
                                Next
                            </Button>
                        </Box>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default CustProSalesReport;